import { format } from 'date-fns';
import { Clock, User, CheckCircle2, Eye, Calendar } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface AppointmentCardProps {
  appointment: {
    id: string;
    name: string;
    email: string;
    service_type: string;
    appointment_type?: string;
    preferred_time?: string;
    status: string;
    duration_minutes?: number;
    completed_at?: string;
    requires_approval?: boolean;
  };
  onView?: (appointment: any) => void;
  onComplete?: (appointment: any) => void;
  onReschedule?: (appointment: any) => void;
}

const getStatusColor = (status: string, completedAt?: string, requiresApproval?: boolean) => {
  if (completedAt || status === 'completed') return 'bg-green-500/10 border-green-500/20 text-green-700';
  if (status === 'confirmed') return 'bg-blue-500/10 border-blue-500/20 text-blue-700';
  if (status === 'pending' && requiresApproval) return 'bg-orange-500/10 border-orange-500/20 text-orange-700';
  if (status === 'pending') return 'bg-gray-500/10 border-gray-500/20 text-gray-700';
  if (status === 'cancelled') return 'bg-red-500/10 border-red-500/20 text-red-700';
  if (status === 'pending_approval') return 'bg-orange-500/10 border-orange-500/20 text-orange-700';
  if (status === 'modification_requested') return 'bg-yellow-500/10 border-yellow-500/20 text-yellow-700';
  return 'bg-muted/50 border-border';
};

export default function AppointmentCard({ appointment, onView, onComplete, onReschedule }: AppointmentCardProps) {
  const isCompleted = appointment.completed_at || appointment.status === 'completed';
  const cardColor = getStatusColor(appointment.status, appointment.completed_at, appointment.requires_approval);

  return (
    <Card
      className={cn(
        'p-3 border-2 transition-all hover:shadow-md',
        cardColor
      )}
    >
      <div className="space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 flex-shrink-0" />
              <h4 className="font-semibold text-sm truncate">{appointment.name}</h4>
            </div>
            <p className="text-xs text-muted-foreground truncate mt-1">
              {appointment.service_type}
            </p>
          </div>
          {isCompleted && (
            <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
          )}
        </div>

        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" />
          <span>{appointment.preferred_time || 'Time TBD'}</span>
          {appointment.duration_minutes && (
            <span>• {appointment.duration_minutes}min</span>
          )}
        </div>

        <div className="flex items-center gap-1 pt-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => onView?.(appointment)}
                >
                  <Eye className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>View Details</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {!isCompleted && onReschedule && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => onReschedule(appointment)}
                  >
                    <Calendar className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Reschedule</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          {!isCompleted && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    size="sm"
                    className="h-8 w-8 p-0 bg-green-600 hover:bg-green-700"
                    onClick={() => onComplete?.(appointment)}
                  >
                    <CheckCircle2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Complete</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      </div>
    </Card>
  );
}
